import { useEffect, useMemo, useRef, useState, type ChangeEvent, type DragEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Spinner } from '@/components/ui/Spinner';
import { Button } from '@/components/ui/Button';
import { CandidatesTable } from '@/components/candidates/CandidatesTable';
import {
  useCandidateRuns,
  useCandidateRun,
  useCandidateScoreboard,
  useUploadCandidateRun,
  useUnlinkCandidate,
} from '@/hooks/queries/useCandidates';

const rate = (n: number | null | undefined) => (n == null ? '—' : `${Math.round(n * 100)}%`);
const money = (n: number | null | undefined) => (n == null ? '—' : n.toFixed(2));
const fileSize = (bytes: number) => `${(bytes / 1024).toFixed(1)} KB`;

export function CandidatesPage() {
  const { runId: routeRunId = null } = useParams<{ runId: string }>();
  const navigate = useNavigate();
  const runs = useCandidateRuns();
  const board = useCandidateScoreboard();
  const upload = useUploadCandidateRun();
  const unlink = useUnlinkCandidate();

  // The runs list is the source of truth for "does this id exist" -- the detail endpoint would
  // also 404 on an unknown id, but routing the not-found decision through it would fire a
  // detail fetch (and its own error toast) for a path segment that was never a real run.
  const knownRunIds = useMemo(() => runs.data?.runs.map((r) => r.id) ?? [], [runs.data]);
  const runsLoaded = runs.data !== undefined;
  // Gated on a settled list, not just a loaded one: right after a successful upload the page
  // navigates to the new run's id while invalidateQueries's refetch is still in flight, so the
  // list still holds its pre-upload data for a moment. Without the isFetching check, that
  // stale list would call the brand-new id unknown and flash "Run not found" over the table.
  const runNotFound = runsLoaded && !runs.isFetching && routeRunId !== null && !knownRunIds.includes(routeRunId);
  const effectiveRunId = runNotFound ? null : routeRunId;
  const run = useCandidateRun(effectiveRunId);

  useEffect(() => {
    if (routeRunId !== null || !runsLoaded) return;
    const latest = runs.data?.runs[0];
    if (latest) navigate(`/candidates/${latest.id}`, { replace: true });
  }, [routeRunId, runsLoaded, runs.data, navigate]);

  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pastedText, setPastedText] = useState('');
  const [status, setStatus] = useState<string | null>(null);
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  // Counts nested enter/leave pairs so dragging over a child element (the picker area, a table
  // row) doesn't flicker the overlay off -- a plain boolean toggled by dragEnter/dragLeave would
  // drop back to false the moment the pointer crosses from the page root onto any child.
  const dragDepth = useRef(0);

  // A drag that ends outside the page (dropped on the desktop, cancelled with Escape, dragged out
  // of the window) never sends the matching dragleave, so the counter would sit above zero and
  // the overlay would stay up. The window hears the end of every drag; reset on it.
  useEffect(() => {
    const reset = () => {
      dragDepth.current = 0;
      setIsDraggingFile(false);
    };
    window.addEventListener('dragend', reset);
    window.addEventListener('drop', reset);
    return () => {
      window.removeEventListener('dragend', reset);
      window.removeEventListener('drop', reset);
    };
  }, []);

  const takeFile = (file: File | null) => {
    setPendingFile(file);
    // A file and pasted text are exclusive -- picking a file retires whatever was pasted, and
    // closes the paste panel rather than leaving it open over now-stale text.
    setPastedText('');
    setPasteOpen(false);
    // A fresh choice retires whatever the last upload attempt reported.
    setStatus(null);
  };

  const onFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    takeFile(e.target.files?.[0] ?? null);
  };

  const onDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const finishDrop = (file: File | null) => {
    dragDepth.current = 0;
    setIsDraggingFile(false);
    if (file) takeFile(file);
  };

  // The picker area's own drop target (predates the whole-page one below). It stops propagation
  // so the page-root handler never also processes the same drop -- one drop, one takeFile call --
  // and resets the drag counter/overlay itself rather than relying on bubbling to do it.
  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    finishDrop(e.dataTransfer.files?.[0] ?? null);
  };

  const onPageDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    dragDepth.current += 1;
    setIsDraggingFile(true);
  };

  const onPageDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setIsDraggingFile(false);
  };

  const onPageDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    finishDrop(e.dataTransfer.files?.[0] ?? null);
  };

  const onPasteChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setPastedText(e.target.value);
    // The reverse exclusion: typing retires any pending file (and its shown name).
    setPendingFile(null);
    if (fileRef.current) fileRef.current.value = '';
    setStatus(null);
  };

  const onUpload = async () => {
    if (!pendingFile && !pastedText.trim()) return;
    setStatus('Uploading…');

    let text: string;
    if (pendingFile) {
      try {
        text = await pendingFile.text();
      } catch {
        setStatus('Could not read that file.');
        return;
      }
    } else {
      text = pastedText;
    }

    let report: unknown;
    try {
      report = JSON.parse(text);
    } catch {
      const message = pendingFile ? 'That file is not valid JSON.' : 'That text is not valid JSON.';
      setStatus(message);
      toast.error(message);
      return;
    }
    upload.mutate(report, {
      onSuccess: (result) => {
        const summary = `${result.replaced ? 'Replaced' : 'Stored'} run: ${result.candidates} candidates, ${result.disqualified} disqualified, ${result.linked} linked`;
        setStatus(summary);
        setPendingFile(null);
        if (fileRef.current) fileRef.current.value = '';
        setPastedText('');
        setPasteOpen(false);
        // uploadRun's return type carries `runId`, so this always has one to navigate to today.
        // The bare-path fallback is kept in case that response shape ever drops it -- the
        // replace-to-latest redirect above would then pick up the new run itself.
        navigate(result.runId ? `/candidates/${result.runId}` : '/candidates');
      },
      onError: (error: Error) => {
        setStatus(error.message || 'Upload rejected');
      },
    });
  };

  return (
    <div
      className="space-y-4"
      data-testid="candidates-page"
      onDragEnter={onPageDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onPageDragLeave}
      onDrop={onPageDrop}
    >
      {/* Whole-page drop target (2026-09-16): a file dragged anywhere over the page, not just
          the picker area, shows this overlay. It's visual only (pointer-events-none) so the
          drag/drop events it would otherwise intercept keep reaching whatever's actually
          underneath the cursor -- the root div above, or the picker area's own handler. */}
      {isDraggingFile && (
        <div
          data-testid="drop-overlay"
          className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center bg-brand-600/10 backdrop-blur-sm"
        >
          <div className="rounded-lg border-2 border-dashed border-brand-500 bg-white px-6 py-4 text-sm font-medium text-brand-700 shadow-lg">
            Drop the report anywhere
          </div>
        </div>
      )}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Candidates</h1>
          <p className="text-sm text-gray-500">
            The options bot's iron condor shortlist, re-priced live. Outcomes are hold-to-expiration at the
            bot's credit — not your realized P&amp;L.
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="flex items-center gap-2">
            {/* Also a drop target (2026-09-16), for the same Safari file-panel workaround below:
                dragging the report onto this area sets it as the pending file exactly as the
                input's change event does. */}
            <div data-testid="report-drop" onDragOver={onDragOver} onDrop={onDrop}>
              {/* A label-driven file picker is the pattern that works on iOS Safari and desktop
                  alike without a programmatic click() -- sr-only hides the real input visually
                  without display:none, which some mobile browsers refuse to open at all. */}
              <label className="inline-flex cursor-pointer items-center justify-center rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 focus-within:ring-2 focus-within:ring-brand-500 focus-within:ring-offset-2">
                Choose report…
                {/* No `accept` here on purpose (2026-09-16): Safari on Mac, after a macOS update,
                    greys out .json entirely under a mixed MIME-plus-extension accept filter, so
                    no file could be chosen and no change event ever fired. Any file may be chosen
                    instead -- the page validates by parsing the contents and reports "That file
                    is not valid JSON." inline for anything that isn't. */}
                <input
                  ref={fileRef}
                  type="file"
                  data-testid="report-file"
                  className="sr-only"
                  onChange={onFileChange}
                />
              </label>
            </div>
            {pendingFile && (
              <span data-testid="report-name" className="text-xs text-gray-500">
                {pendingFile.name} · {fileSize(pendingFile.size)}
              </span>
            )}
            {/* A second workaround for the same Safari bug (2026-09-16): pasting the report's
                JSON text bypasses the file panel entirely. Exclusive with the file picker -- see
                takeFile/onPasteChange. */}
            <button
              type="button"
              data-testid="paste-toggle"
              onClick={() => setPasteOpen((open) => !open)}
              className="text-xs font-medium text-gray-500 underline hover:text-gray-700"
            >
              Paste JSON instead
            </button>
            <Button
              size="sm"
              data-testid="upload"
              onClick={() => void onUpload()}
              disabled={(!pendingFile && !pastedText.trim()) || upload.isPending}
            >
              {upload.isPending ? 'Uploading…' : 'Upload'}
            </Button>
          </div>
          {pasteOpen && (
            <textarea
              data-testid="report-paste"
              value={pastedText}
              onChange={onPasteChange}
              placeholder="Paste the bot's report JSON here"
              rows={4}
              className="w-72 rounded-lg border border-gray-300 px-2 py-1.5 text-xs text-gray-700 focus:ring-2 focus:ring-brand-500 focus:ring-offset-2"
            />
          )}
          {status && (
            <span data-testid="upload-status" className="text-xs text-gray-500">
              {status}
            </span>
          )}
        </div>
      </div>

      {board.data && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6 text-sm">
          <div className="rounded-lg border border-gray-200 p-3">
            <div className="text-xs text-gray-500">Settled</div>
            <div className="text-lg font-semibold">{board.data.allTime.settled}</div>
          </div>
          <div className="rounded-lg border border-gray-200 p-3">
            <div className="text-xs text-gray-500">Finished inside</div>
            <div className="text-lg font-semibold">{rate(board.data.allTime.insideRate)}</div>
          </div>
          <div className="rounded-lg border border-gray-200 p-3">
            <div className="text-xs text-gray-500">Median P&amp;L / share</div>
            <div className="text-lg font-semibold">{money(board.data.allTime.medianPnl)}</div>
          </div>
          <div className="rounded-lg border border-gray-200 p-3">
            <div className="text-xs text-gray-500">Delta exceptions inside</div>
            <div className="text-lg font-semibold">{rate(board.data.byFlag['delta-exception']?.insideRate)}</div>
          </div>
          {/* The two tiles that answer "did trading the bot's picks work out", as opposed to
              "were the bot's picks any good" — the four above. */}
          <div className="rounded-lg border border-gray-200 p-3">
            <div className="text-xs text-gray-500">Traded inside</div>
            <div className="text-lg font-semibold">{rate(board.data.byTraded.traded.insideRate)}</div>
          </div>
          <div className="rounded-lg border border-gray-200 p-3">
            <div className="text-xs text-gray-500">Traded realized / share</div>
            <div className="text-lg font-semibold">{money(board.data.byTraded.traded.realizedMeanPnl)}</div>
          </div>
        </div>
      )}

      {runNotFound && (
        <div className="py-8 text-center text-sm text-gray-500">
          Run not found.{' '}
          <Link to="/candidates" className="text-brand-600 hover:text-brand-700">
            Back to candidates
          </Link>
        </div>
      )}

      {!runNotFound && (
        <>
          {runs.isLoading && (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          )}
          {runs.data && runs.data.runs.length === 0 && (
            <div className="py-8 text-center text-sm text-gray-500">
              No runs yet. Upload the bot's JSON report to start.
            </div>
          )}

          {runs.data && runs.data.runs.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <label className="text-gray-500">Run</label>
              <select
                className="rounded border border-gray-300 px-2 py-1"
                value={effectiveRunId ?? ''}
                onChange={(e) => navigate(`/candidates/${e.target.value}`)}
              >
                {runs.data.runs.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.reportDate} · {r.snapshotAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} ·{' '}
                    {r.counts.candidates} candidates
                    {r.insideRate != null ? ` · ${rate(r.insideRate)} inside` : ''}
                  </option>
                ))}
              </select>
              {run.data && (
                <span className="text-gray-500">
                  {run.data.run.rowsEvaluated} of {run.data.run.rowsInScan} rows evaluated · {run.data.run.postedBy}
                </span>
              )}
            </div>
          )}

          {run.isLoading && (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          )}
          {run.data && (
            <>
              <CandidatesTable
                candidates={run.data.candidates}
                onUnlink={(id) => {
                  // Clearing is remembered, so a stray click would quietly cost the trade its link.
                  if (confirm('Clear this link? The matcher will not re-apply it.')) unlink.mutate(id);
                }}
              />
              {run.data.candidates.length === 0 && (
                <div className="text-sm text-gray-500">No candidates survived this run.</div>
              )}
              <details className="text-sm">
                <summary className="cursor-pointer text-gray-600">
                  Disqualified ({run.data.disqualified.length}) · Not evaluated ({run.data.notEvaluated.length})
                </summary>
                <ul className="mt-2 space-y-1 text-gray-600">
                  {run.data.disqualified.map((d) => (
                    <li key={d.id}>
                      <span className="font-mono">
                        {d.ticker} {d.expiration}
                      </span>
                      : {d.stage} — {d.reason}
                    </li>
                  ))}
                  {run.data.notEvaluated.map((d) => (
                    <li key={d.id}>
                      <span className="font-mono">
                        {d.ticker} {d.expiration}
                      </span>
                      : not evaluated — {d.reason}
                    </li>
                  ))}
                </ul>
              </details>
            </>
          )}
        </>
      )}
    </div>
  );
}
